import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from 'react-redux';
import DateSelect from "../utils/DateSelect";
import PointsSelect from "../utils/PointsSelect";
import MultiSelect from "../utils/MultiSelect";
import Loader from '../utils/Loader';
import Select from 'react-select';
import moment from 'moment';
import ErrorContainer from '../utils/ErrorContainer';
import { fetchUsersByRole } from "../../action/user";
import { roleId, urls } from "../../constants/app";
import { isEmpty } from "lodash";
import { fetchFilials } from "../../action/handbook";
import { fetchRawsList, fetchReportInvoices, setReportsFilters } from "../../action";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { parseNumToFixed } from "../../utils/common";
import { fromStore } from "../../selectors";
import request from "../../action/utils/request";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const styles = {
    loading: {
        minHeight: '200px',
        display: 'flex',
        justifyContent: 'center'
    }
};

const firstLvlOptions = [
    { label: 'Нет', value: 'none' },
    { label: 'День', value: 'day', notRender: [ 'year' ]},
    { label: 'Неделя', value: 'week', notRender: [ 'day', 'week' ]},
    { label: 'Месяц', value: 'month', notRender: [ 'day', 'week' ]},
];

const PointGraph = React.memo(({ point, rawId, rawName, labels, invoices, lastInvoices, setLastInvoices, raws, getDateKeyInvoice }) => {
    const [graphData, setGraphData] = useState(null);
    const [warning, setWarning] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const getPriceRaw = useCallback(async (pointId, rawId) => {
        try {
            const params = {
                point: pointId,
                _sort: 'date:desc',
                rawId,
                ignoreBalance: true,
            };
            const key = `${pointId}_${rawId}`;
            const savedInvoice = lastInvoices[key];
            if (savedInvoice) {
                const invoiceRaw = savedInvoice.rawMaterials ? savedInvoice.rawMaterials.find((r) => r.id === rawId) : null;
                if (invoiceRaw && invoiceRaw.price) {
                    const invoiceDate = savedInvoice.date ? moment(savedInvoice.date).format('DD.MM.YYYY HH:mm') : moment(savedInvoice.created_at).format('DD.MM.YYYY HH:mm');
                    return {
                        text: `* Данные за период отсутствуют. Отображена цена из накладной ${savedInvoice.number ? `№ ${savedInvoice.number || ''}` : ''} от ${invoiceDate}`,
                        price: invoiceRaw.price,
                    };
                }
            }

            const lastInvoice = await request({ method: 'get', url: `${urls.invoiceforms}/getNotRealizationInventory`, params }); //чет не работает нихуя
            if (lastInvoice && !lastInvoice.error) {
                const invoiceRaw = lastInvoice.rawMaterials ? lastInvoice.rawMaterials.find((raw) => raw.id === rawId) : null;
                if (invoiceRaw && invoiceRaw.price) {
                    setLastInvoices((prev) => ({ ...prev, [key]: lastInvoice }));
                    const invoiceDate = lastInvoice.date ? moment(lastInvoice.date).format('DD.MM.YYYY HH:mm') : moment(lastInvoice.created_at).format('DD.MM.YYYY HH:mm');
                    return {
                        text: `* Данные за период отсутствуют. Отображена цена из накладной ${lastInvoice.number ? `№ ${lastInvoice.number || ''}` : ''} от ${invoiceDate}`,
                        price: parseNumToFixed(invoiceRaw.price),
                    };
                }
            }
            let entityRaw = raws.find((raw) => raw.id === rawId);
            if (!entityRaw || !entityRaw.last_price) entityRaw = await request({ method: 'get', url: `${urls.rawmaterials}/${rawId}` });
            return {
                text: '* Данные за период отсутствуют. Отображена актуальная цена сырья (по последней существующей накладной в системе).',
                price: parseNumToFixed(entityRaw.last_price),
            };
        } catch (err) {
            let entityRaw = raws.find((raw) => raw.id === rawId);
            if (!entityRaw || !entityRaw.last_price) entityRaw = await request({ method: 'get', url: `${urls.rawmaterials}/${rawId}` });
            return {
                text: '* Данные за период отсутствуют. Отображена актуальная цена сырья (по последней существующей накладной в системе).',
                price: parseNumToFixed(entityRaw.last_price),
            };
        }
    }, [lastInvoices, raws, setLastInvoices]);

    useEffect(() => {
        let isMounted = true;
        const getData = async () => {
            if (!rawId || !labels || labels.length === 0) {
                setError('Не выбрано сырьё или период');
                return;
            }

            setLoading(true);
            setError('');
            setWarning('');

            try {
                const filteredInvoices = invoices.filter((invoice) => {
                    const pointId = invoice.point && invoice.point.id ? invoice.point.id : invoice.point;
                    if (pointId !== point.value) return false;
                    if (!invoice.rawMaterials || invoice.rawMaterials.length === 0) return false;
                    return invoice.rawMaterials.some((item) => item.id === rawId);
                });

                let lastDataPirce = 0;

                let dataPrices = labels.map((date) => {
                    const dateInvoices = filteredInvoices.filter((invoice) => getDateKeyInvoice(invoice) === date);
                    if (dateInvoices.length === 0) return lastDataPirce;
                    const max = dateInvoices.reduce((acc, invoice) => {
                        const raw = invoice.rawMaterials.find((item) => item.id === rawId);
                        return (raw && raw.price) > acc ? parseNumToFixed(raw.price) : acc;
                    }, 0);
                    lastDataPirce = max;
                    return max;
                });

                if (dataPrices.every((price) => !price || price === 0)) {
                    const lastPriceData = await getPriceRaw(point.value, rawId);
                    if (lastPriceData && lastPriceData.price) {
                        dataPrices = labels.map(() => lastPriceData.price);
                        if (lastPriceData.text) setWarning(lastPriceData.text);
                    }
                }

                if (isMounted) {
                    setGraphData({
                        labels,
                        datasets: [
                            {
                                label: rawName,
                                data: dataPrices,
                                borderColor: '#5867dd',
                                backgroundColor: '#5867dd',
                            },
                        ],
                    });
                }
            } catch (err) {
                if (isMounted) setError('Ошибка загрузки данных графика');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        getData();

        return () => { isMounted = false; };
    }, [point, rawId, rawName, labels, invoices, getDateKeyInvoice, getPriceRaw]);

    if (loading) return <Loader />;
    if (error) return <div className="kt-portlet"><div className="kt-portlet__body" style={{ color: 'red' }}>{`Ошибка загрузки для филиала ${point.label} - ${error}`}</div></div>;
    if (!graphData) return null;

    let maxYScale = 1;
    if (graphData && graphData.datasets && graphData.datasets[0] && graphData.datasets[0].data) {
    const dataValues = graphData.datasets[0].data;
    if (dataValues.length > 0) {
        const maxData = Math.max(...dataValues);
        maxYScale = Math.ceil(maxData * 1.1 / 5) * 5;
        if (maxYScale === 0) maxYScale = 1;
    }
    }

    const options = {
        responsive: true,
        plugins: {
            legend: { 
                position: 'top',
            },
            title: { 
                display: true,
                text: point.label,
            }
        },
        scales: {
            y: {
                min: 0,
                max: maxYScale,
            }
        }
    };

    return (
        <>
            <Line options={options} data={graphData} />
            {warning && (
                <div className="kt-portlet">
                    <div className="kt-portlet__body" style={{ color: "#c79004" }}>{warning}</div>
                </div>
            )}
            <div className="kt-separator kt-separator--border kt-separator--space-sm"/>
        </>
    );
});

export default function DynamicsPrices() {
    const dispatch = useDispatch();

    const data = useSelector(state => state.reports.dynamicsPrices);
    const points = useSelector(state => state.handbook.filials);
    const pointsCount = useSelector(state => state.handbook.count.filials);
    const pointLoading = useSelector(state => state.loaders.filials);
    const raws = useSelector(fromStore.rawListSelector);

    const { invoices, filters, loading } = data;
    const [ regionalDirectors, setRegionalDirectors ] = useState([]);
    const [ renderFilters, setRenderFilters ] = useState(filters);
    const [ lastInvoices, setLastInvoices ] = useState({});
    const [ renderDetail, setRenderDetail ] = useState({ firstLvl: {label: 'Нет', value: 'none'} });

    useEffect(() => {
        const getData = async () => {
            const regDirs = await dispatch(fetchUsersByRole({ role: roleId.regionalDirector }));
            const resultDirs = regDirs.map((dir) => ({
                value: dir.id,
                label: dir.name ? dir.name : dir.username,
                points: dir.points
            })).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
            setRegionalDirectors(resultDirs);
            if (!pointLoading && (isEmpty(points) || points.length < pointsCount)) await dispatch(fetchFilials(null, true));
        };

        if (isEmpty(raws)) dispatch(fetchRawsList());
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangeDateSelect = (e) => {
        if (e && e.min && e.max) {
            const updated = { ...filters };
            updated.created_at_gte = e.min;
            updated.created_at_lte = e.max;
            dispatch(setReportsFilters('dynamicsPrices', updated));
        }
    };

    const setDetail = (field) => (data) => {
        const updated = { ...renderDetail, [field]: data };
        setRenderDetail(updated);
    };

    const onSearch = () => {
        dispatch(fetchReportInvoices({}, 'dynamicsPrices'));
        setRenderFilters(filters);
    };

    const getFirstLvlOptions = () => {
        const date_gte = moment(filters.created_at_gte);
        const date_lte = moment(filters.created_at_lte);
        const diff = date_lte.diff(date_gte, 'days');
        let value = null;
        if (diff === 0) value = 'day';
        if (diff === 6) value = 'week';
        if (diff > 6 && diff < 180) value = 'month';
        if (diff >= 180) value = 'year';

        return value ? firstLvlOptions.filter((item) => !item.notRender || !item.notRender.includes(value)) : firstLvlOptions;
    };

    const isDisabledSearch = () => {
        if (loading) return true;
        if ((!filters.points || filters.points.length === 0) && (!filters.regionalDirectors || filters.regionalDirectors.length === 0)) return true;
        return false;
    };

    const setFilters = (field) => (data) => {
        const updated = { ...filters, [field]: data };
        dispatch(setReportsFilters('dynamicsPrices', updated));
    };

    const renderAdress = (item) => {
        if (!item) return 'Не определен';
        const cityName = item.city && item.city.name ? item.city.name : '---';
        return `${cityName}, ${item.street}, ${item.house}`;
    };

    const getDateKeys = useCallback(() => {
        if (!renderDetail.firstLvl || !renderDetail.firstLvl.value || renderDetail.firstLvl.value === 'none') return null;

        const startDate = moment(renderFilters.created_at_gte);
        const endDate = moment(renderFilters.created_at_lte);
        const dateKeys = [];

        switch (renderDetail.firstLvl.value) {
            case 'day': {
                let current = startDate.clone();
                while (current <= endDate) {
                    dateKeys.push(current.format('DD.MM.YYYY'));
                    current.add(1, 'day');
                }
                break;
            }
            case 'week': {
                let current = startDate.clone().startOf('isoWeek');
                let end = endDate.clone().endOf('isoWeek');

                while (current <= end) {
                    let weekStart = current;
                    let weekEnd = current.clone().endOf('isoWeek');

                    if (weekStart < startDate) {
                        weekStart = startDate.format('DD.MM.YYYY');
                    } else {
                        weekStart = weekStart.format('DD.MM.YYYY');
                    }

                    if (weekEnd > endDate) {
                        weekEnd = endDate.format('DD.MM.YYYY');
                    } else {
                        weekEnd = weekEnd.format('DD.MM.YYYY');
                    }

                    dateKeys.push(`${weekStart} - ${weekEnd}`);
                    current.add(1, 'week');
                }
                break;
            }
            case 'month': {
                let current = startDate.clone().startOf('month');
                let end = endDate.clone().endOf('month');

                while (current <= end) {
                    const monthStart = current.format('DD.MM.YYYY');
                    const monthEnd = current.clone().endOf('month').format('DD.MM.YYYY');
                    dateKeys.push(`${monthStart} - ${monthEnd}`);
                    current.add(1, 'month');
                }
                break;
            }
            default: break;
        }

        return dateKeys;
    }, [renderDetail.firstLvl, renderFilters.created_at_gte, renderFilters.created_at_lte]);

    const getDateKeyInvoice = useCallback((invoice) => {
        const invoiceDate = invoice.date ? moment(invoice.date) : moment(invoice.created_at);

        switch (renderDetail.firstLvl.value) {
            case 'day': {
                return invoiceDate.format('DD.MM.YYYY');
            }
            case 'week': {
                const startDate = moment(renderFilters.created_at_gte);
                const endDate = moment(renderFilters.created_at_lte);
                let weekStart = invoiceDate.clone().startOf('isoWeek');
                let weekEnd = invoiceDate.clone().endOf('isoWeek');

                if (weekStart < startDate) {
                    weekStart = startDate.format('DD.MM.YYYY');
                } else {
                    weekStart = weekStart.format('DD.MM.YYYY');
                }

                if (weekEnd > endDate) {
                    weekEnd = endDate.format('DD.MM.YYYY');
                } else {
                    weekEnd = weekEnd.format('DD.MM.YYYY');
                }

                return `${weekStart} - ${weekEnd}`;
            }
            case 'month': {
                const monthStart = invoiceDate.startOf('month').format('DD.MM.YYYY');
                const monthEnd = invoiceDate.endOf('month').format('DD.MM.YYYY');
                return `${monthStart} - ${monthEnd}`;
            }
            default: {
                return null;
            }
        }
    }, [renderDetail.firstLvl, renderFilters.created_at_gte, renderFilters.created_at_lte]);

    const labels = useMemo(() => getDateKeys(), [getDateKeys]);

    const rawId = useMemo(() => {
        if (!renderDetail.secondLvl || !renderDetail.secondLvl.value || renderDetail.secondLvl.value === 'none') return null;
        return parseInt(renderDetail.secondLvl.value);
    }, [renderDetail.secondLvl]);

    const rawName = useMemo(() => {
        if (!rawId) return '';
        const dataRaw = raws.find(raw => raw.id === rawId);
        return dataRaw && dataRaw.name ? dataRaw.name : `Сырье id ${rawId}`;
    }, [rawId, raws]);

    const renderPoints = useMemo(() => {
        let pointsList = [];
        if (renderFilters.points && renderFilters.points.length > 0) {
            pointsList = renderFilters.points
                .map((filter) => {
                    const dataPoint = points.find((point) => point.id === filter.value);
                    return dataPoint ? { value: dataPoint.id, label: renderAdress(dataPoint) } : null;
                })
                .filter(Boolean);
        } else if (renderFilters.regionalDirectors && renderFilters.regionalDirectors.length > 0) {
            renderFilters.regionalDirectors.forEach((dir) => {
                if (dir && dir.points && dir.points.length > 0) {
                    dir.points.forEach((point) => {
                        if (point) {
                            const pointId = point.id ? point.id : point;
                            const dataPoint = points.find((p) => p.id === pointId);
                            if (dataPoint) {
                                pointsList.push({ value: dataPoint.id, label: renderAdress(dataPoint) });
                            }
                        }
                    });
                }
            });
        } else if (invoices && invoices.length > 0) {
            const uniquePointIds = [];
            invoices.forEach(inv => {
                const pointId = inv.point && inv.point.id ? inv.point.id : inv.point;
                if (uniquePointIds.indexOf(pointId) === -1) uniquePointIds.push(pointId);
            });
            uniquePointIds.forEach(pointId => {
                const dataPoint = points.find(p => p.id === pointId);
                if (dataPoint) pointsList.push({ value: dataPoint.id, label: renderAdress(dataPoint) });
            });
        }
        return pointsList;
    }, [renderFilters, points, invoices]);

    const renderHead = () => {
        return (
            <div className="kt-portlet__head" style={{ flexDirection: 'column', gap: '10px' }}>
                <div className="kt-portlet__head-label">
                    <h3 className="kt-portlet__head-title">
                        Динамика цен на сырье
                    </h3>
                </div>
                <div className='kt-separator kt-separator--border-dashed kt-separator--space-sm' />
                <div><h6 style={{ color: 'black' }}> Поиск </h6></div>
                <div className="kt-portlet__head-toolbar" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ width: 200 }}>
                        <label>Период *</label>
                        <DateSelect
                            isDisabled={loading}
                            isLoading={loading}
                            optionsDate={{
                                tommorow: true,
                                period: true,
                                prevWeek: true,
                                prevMonth: true,
                            }}
                            limit={{
                                field: 'months',
                                value: 6
                            }}
                            width={200}
                            onChange={onChangeDateSelect}
                        />
                    </div>
                    <div>
                        <label>Филиалы {(filters.regionalDirectors && filters.regionalDirectors.length > 0) ? '' : '*'}</label>
                        <PointsSelect
                            width={200}
                            placeholder='Филиалы'
                            values={filters.points}
                            setData={setFilters('points')}
                            disabled={loading || (filters.regionalDirectors && filters.regionalDirectors.length > 0)}
                        />
                    </div>
                    <div style={{ width: 200 }}>
                        <label>Региональный директор {(filters.points && filters.points.length > 0) ? '' : '*'}</label>
                        <MultiSelect
                            data={regionalDirectors}
                            width={200}
                            placeholder='Рег. директор'
                            values={filters.regionalDirectors}
                            setData={setFilters('regionalDirectors')}
                            loading={loading}
                            disabled={loading || (filters.points && filters.points.length > 0)}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            disabled={isDisabledSearch()}
                            onClick={onSearch}
                            style={{ marginTop: '1.9rem' }}
                            className="btn btn-primary"
                        >
                            Найти
                        </button>
                    </div>
                </div>
                <div className='kt-separator kt-separator--border-dashed kt-separator--space-sm' />
                <div><h6 style={{ color: 'black' }}> Детализация </h6></div>
                <div className="kt-portlet__head-toolbar" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ width: 200 }}>
                        <label>Уровень 1 *</label>
                        <Select
                            closeMenuOnSelect={true}
                            options={getFirstLvlOptions()}
                            onChange={setDetail('firstLvl')}
                            placeholder="Уровень 1"
                            noOptionsMessage={() => 'Нет'}
                            value={renderDetail.firstLvl}
                        />
                    </div>
                    <div style={{ width: 200 }}>
                        <label>Сырье *</label>
                        <Select
                            closeMenuOnSelect={true}
                            options={raws.map((el) => ({
                                value: el.id,
                                label: el.name,
                            }))}
                            onChange={setDetail('secondLvl')}
                            placeholder="Сырье"
                            noOptionsMessage={() => 'Нет сырья'}
                            value={renderDetail.secondLvl}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderBody = () => {
        if (loading || pointLoading) return <div style={styles.loading}><Loader /></div>;

        const isDetailInvalid = !renderDetail.firstLvl || !renderDetail.firstLvl.value || renderDetail.firstLvl.value === 'none';
        const isRawInvalid = !renderDetail.secondLvl || !renderDetail.secondLvl.value || renderDetail.secondLvl.value === 'none';


        if (renderPoints.length === 0) return <div className="kt-portlet"><div className="kt-portlet__body">Нет данных для отображения</div></div>;

        if (isDetailInvalid || isRawInvalid) {
            return (
                <div className="kt-portlet">
                    <div className="kt-portlet__body" style={{ color: 'red' }}>
                        {isDetailInvalid && '* Не выбрана детализация. '}
                        {isRawInvalid && '* Не выбрано сырье.'}
                    </div>
                </div>
            );
        }

        if (!labels || labels.length === 0) return <div className="kt-portlet"><div className="kt-portlet__body">Нет данных для выбранного периода</div></div>;

        return (
            <div className="kt-portlet">
                <ErrorContainer field="delivery" style={{ margin: '20px 20px 0px 20px' }} hasCloseButton={true} />
                <div className='kt-portlet__body'>
                    {renderPoints.map((point) => (
                        <PointGraph
                            key={point.value}
                            point={point}
                            rawId={rawId}
                            rawName={rawName}
                            labels={labels}
                            invoices={invoices}
                            lastInvoices={lastInvoices}
                            setLastInvoices={setLastInvoices}
                            raws={raws}
                            getDateKeyInvoice={getDateKeyInvoice}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="kt-portlet">
                {renderHead()}
            </div>
            {renderBody()}
        </>
    );
}